import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getTranslate } from 'react-localize-redux';

import { withStyles, Typography, TextField } from '@material-ui/core';

import urls from '../../app/urls';
import WeekdaySelect from './components/WeekdaySelect';
import * as PatientStore from '../../app/store/PatientStore';
import { patientUserLevels } from '../../youwell-common/constants';
import { hasValue } from '../../youwell-common/objectUtils';
import { trimIfString } from '../../youwell-common/stringUtils';
import { TopActionBar, BottomActionBar } from '../../common/components';
import ResizableList from '../../common/components/ResizableList';
import { validateAllFields, validateField, validateRequired } from '../../youwell-common/validationUtils';
import {
    hasErrors, isContactEmpty, stripEmptyProperties,
} from './components/utils';
import ContactPersons from './components/ContactPersons';

const styles = theme => ({
    root: {
    },
    content: {
        padding: theme.spacing(4),

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    topContent: {
        display: 'flex',

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    topContentBox: {
        flex: '1 1 auto',
        display: 'flex',
        margin: theme.spacing(1),
        marginBottom: theme.spacing(5),

        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(3),
            width: '100%',
            padding: theme.spacing(1),
        },

        '& + &': {
            marginLeft: theme.spacing(4),
            [theme.breakpoints.down('xs')]: {
                margin: theme.spacing(1),
                marginBottom: theme.spacing(3),
            },
        },
    },
    textField: {
        flex: '1 1 auto',
    },
    textFieldSmall: {
        width: 160,
        marginLeft: theme.spacing(2),

        [theme.breakpoints.down('sm')]: {
            width: 100,
        },
    },
    textFieldDescription: {
        marginBottom: theme.spacing(5),
        [theme.breakpoints.down('xs')]: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(4),
        },
    },
    resourceStudents: {
        marginBottom: theme.spacing(4),
    },
    contentDescription: {

    },
});

const createResourceStudent = () => ({
    name: '',
    class: '',
    key: Date.now(), // should have been 'id' but legacy..
});

const defaultState = {
    name: '',
    description: '',
};

const defaultExtendedProps = {
    class: '',
    assignedHours: '',
};

const specialExtendedProps = {
    availableWeekdays: [],
    resourceStudents: [],
};

const validators = {
    name: validateRequired,
};


const stateFromData = (data) => {
    const initialState = {
        ...defaultState,
        ...defaultExtendedProps,
        ...specialExtendedProps,
        contactPersons: [],
        validationErrors: {},
        updated: data?.updated,
        hasUnsavedChanges: false,
        saveMessage: '',
    };

    if (!data) {
        return initialState;
    }

    const extProps = Object.keys(defaultExtendedProps).reduce(
        (obj, key) => (data.extendedProperties && hasValue(data.extendedProperties[key])
            ? { ...obj, [key]: data.extendedProperties[key] } : { ...obj }),
        {
            availableWeekdays: data.extendedProperties?.availableWeekdays?.split(',') || [],
            resourceStudents: data.extendedProperties?.resourceStudents ? JSON.parse(data.extendedProperties.resourceStudents) : [],
        },
    );

    return Object.keys(defaultState).reduce(
        (obj, key) => (hasValue(data[key]) ? { ...obj, [key]: data[key] } : { ...obj }),
        {
            ...initialState,
            ...extProps,
            contactPersons: data.contactPersons || [],
        },
    );
};

class EditStudent extends React.Component {
    state = stateFromData(this.props.studentData);

    static getDerivedStateFromProps(props, state) {
        if (props.studentData?.updated && props.studentData.updated !== state.updated) {
            return stateFromData(props.studentData);
        }

        return null;
    }

    handleChange = name => (event) => {
        this.setState({
            [name]: event.target.value,
            hasUnsavedChanges: true,
            saveMessage: '',
        }, () => {
            if (validators[name]) {
                const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    };

    save = () => {
        const validationErrors = validateAllFields(this.state, validators);

        if (!hasErrors(validationErrors)) {
            const extendedProperties = Object.keys(defaultExtendedProps).reduce(
                (obj, key) => (hasValue(this.state[key])
                    ? { ...obj, [key]: this.state[key] } : { ...obj }),
                {
                    ...this.props.studentData?.extendedProperties,
                    availableWeekdays: this.state.availableWeekdays?.join(',') || null,
                    resourceStudents: this.state.resourceStudents?.length > 0 ? JSON.stringify(this.state.resourceStudents.map(student => stripEmptyProperties(student))) : null,
                },
            );

            const data = Object.keys(defaultState).reduce(
                (obj, key) => (hasValue(this.state[key]) ? { ...obj, [key]: trimIfString(this.state[key]) } : { ...obj }),
                {
                    extendedProperties,
                    userLevel: patientUserLevels.none,
                    contactPersons: this.state.contactPersons.filter(contact => !isContactEmpty(contact)),
                },
            );

            const callback = () => {
                this.setState({
                    hasUnsavedChanges: false,
                    saveMessage: '',
                });
            };

            if (this.props.studentData) {
                data.id = this.props.studentData.id;
                this.props.updateElement(data, {
                    callback,
                    redirectWithElementId: urls.inside.student.home,
                });
            } else {
                this.props.insertElement(data, {
                    callback,
                    redirectWithElementId: urls.inside.student.home,
                });
            }
        } else {
            this.setState({ validationErrors });
        }
    }

    render() {
        const {
            classes, translate, studentData, clientConfig,
        } = this.props;

        const isNew = !studentData;

        const getError = (name) => {
            const error = this.state.validationErrors[name];
            return error ? translate(error) : null;
        };

        return (
            <div className={classes.root}>
                <TopActionBar
                    title={translate(`studentEdit.title_${isNew ? 'new' : 'edit'}`)}
                    onSave={this.save}
                    saveEnabled={this.state.hasUnsavedChanges}
                    backLink={{
                        to: isNew ? urls.inside.home : `${urls.inside.student.home}/${studentData.id}`,
                        text: isNew ? translate('student.backToOverview') : translate('student.backToStudent'),
                    }}
                />

                <div className={classes.content}>
                    <div className={classes.topContent}>
                        <div className={classes.topContentBox}>
                            <TextField
                                id="name"
                                className={classes.textField}
                                label={translate('studentEdit.name')}
                                value={this.state.name}
                                onChange={this.handleChange('name')}
                                error={!!getError('name')}
                                helperText={getError('name')}
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                id="class"
                                className={classes.textFieldSmall}
                                label={translate('studentEdit.class')}
                                value={this.state.class}
                                onChange={this.handleChange('class')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div className={classes.topContentBox}>
                            <WeekdaySelect
                                id="availableWeekdays"
                                className={classes.textField}
                                value={this.state.availableWeekdays}
                                onChange={this.handleChange('availableWeekdays')}
                            />
                            <TextField
                                id="assignedHours"
                                className={classes.textFieldSmall}
                                type="number"
                                label={translate('studentEdit.assignedHours')}
                                value={this.state.assignedHours}
                                onChange={this.handleChange('assignedHours')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                    </div>
                    <div className={classes.descriptionContent}>
                        <TextField
                            id="studentDescription"
                            className={classes.textFieldDescription}
                            multiline
                            fullWidth
                            label={translate('studentEdit.description')}
                            value={this.state.description}
                            onChange={this.handleChange('description')}
                            minRows="8"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            placeholder={translate('studentEdit.descriptionPlaceholder')}
                        />
                    </div>

                    <div className={classes.resourceStudents}>
                        <Typography variant="h6">{translate('studentEdit.resourceStudents')}</Typography>
                        <Typography variant="caption">{translate('studentEdit.resourceStudentsDescription')}</Typography>
                        <ResizableList
                            values={this.state.resourceStudents}
                            onChange={this.handleChange('resourceStudents')}
                            addFunction={createResourceStudent}
                            fieldDefs={[
                                {
                                    name: 'name', label: 'studentEdit.name', type: 'text', required: true,
                                },
                                { name: 'class', label: 'studentEdit.class' },
                            ]}
                        />
                    </div>

                    {!clientConfig.u_AnonymousOnly && (
                        <div className={classes.contacts}>
                            <Typography variant="h6">{translate('studentEdit.contacts')}</Typography>
                            <Typography variant="caption">{translate('studentEdit.contactsDescription')}</Typography>
                            <ContactPersons
                                values={this.state.contactPersons}
                                onChange={this.handleChange('contactPersons')}
                            />
                        </div>
                    )}
                </div>

                <BottomActionBar
                    cancelLink={isNew ? urls.inside.home : `${urls.inside.student.home}/${this.props.match.params.studentId}`}
                    onSave={this.save}
                    saveEnabled={this.state.hasUnsavedChanges}
                />
            </div>
        );
    }
}

EditStudent.propTypes = {
    classes: PropTypes.object.isRequired,
    clientConfig: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    insertElement: PropTypes.func.isRequired,
    updateElement: PropTypes.func.isRequired,
    studentData: PropTypes.object,
    match: PropTypes.shape({
        params: PropTypes.shape({
            studentId: PropTypes.string,
        }).isRequired,
    }).isRequired,
};

EditStudent.defaultProps = {
    studentData: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
    studentData: state.patient.details && state.patient.details[ownProps.match.params.studentId],
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PatientStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EditStudent));
